import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const trueprofit: Competitor = {
  "id": "trueprofit",
  "name": "TrueProfit",
  "group": "ecomm",
  "website": "https://trueprofit.io",
  "owner": "Not stated on the vendor's About page. The Shopify App Store developer footer names FIREAPPS JSC (Ho Chi Minh City, Vietnam). No acquisition was found.",
  "summary": "A real-time net profit app for Shopify stores, with separate support for TikTok Shop, that pulls in ad spend, COGS, shipping and custom costs. It is aimed at Shopify DTC and dropshipping merchants.",
  "bestFor": [
    "Shopify DTC and dropshipping/print-on-demand stores that want a live profit dashboard with almost no setup",
    "Merchants who spend across many ad networks (Meta, Google, TikTok, Microsoft, Pinterest, Snapchat, AppLovin and others)",
    "Owners who check numbers on their phone (iOS and Android apps are included on all plans)",
    "Stores that also sell on TikTok Shop and want profit tracked there too"
  ],
  "notFor": [
    "Brands with meaningful Amazon marketplace revenue (Amazon is supported only for ad spend)",
    "Brands that need wholesale, retail or ERP data combined with DTC",
    "Teams that need SQL access to raw or modeled data, or a warehouse feed"
  ],
  "strengths": [
    "Low entry cost compared with a custom build: $35/month for 300 orders, with a 14-day trial",
    "Ad spend sync from a long list of networks is ready to use, which would each be a separate pipeline in a custom build",
    "Many cost sources are prebuilt: shipping (ShipStation, ShipBob, ShipHero and others), POD and dropship suppliers (Printful, Printify, CJ and others), weight-based shipping rules, and fixed and variable custom costs",
    "A first-party pixel for product analytics (Advanced and up) and a marketing attribution module (Enterprise)",
    "Native iOS and Android apps on every plan",
    "A read-only MCP server for Claude, ChatGPT and OpenClaw (Enterprise plan per the pricing page)",
    "Custom metrics and CLV cohort analysis are included on all plans"
  ],
  "limitations": [
    "Amazon is supported for ad spend only. Amazon marketplace sales are not listed as a sales channel",
    "Sales channels listed are Shopify and TikTok Shop only. No WooCommerce, wholesale, POS or ERP sources are listed",
    "Each store needs its own plan, though stores can be viewed together",
    "Marketing attribution and the MCP/AI connection are Enterprise-only ($200/month)",
    "Data export is CSV. No warehouse sync, SQL access or public API is listed on the pricing or integrations pages",
    "Order-based overage applies on every tier ($0.07-$0.30 per extra order, capped at $300-$1,000/month)",
    "There is no annual plan option"
  ],
  "pricing": {
    "model": "Tiered subscription, priced by monthly order volume with per-order overage",
    "publicStartingPrice": "$35/month (Basic, 300 orders included)",
    "freeTier": "no - 14-day free trial on all plans via the Shopify App Store",
    "notes": "Basic $35/mo (300 orders; $0.30 per extra order, max $300 surcharge; 3 team members; 1 ad account per channel). Advanced $60 (600 orders; $0.20, max $500; adds pixel product analytics, P&L, email reports). Ultimate $100 (1,500 orders; $0.10, max $700; ad sync rules, unlimited COGS zones). Enterprise $200 (3,500 orders; $0.07, max $1,000; marketing attribution, MCP, unlimited users, 10 ad accounts per channel). Cost is driven by order volume, then by ad accounts per channel, team seats, and the number of stores (one plan per store; discounts available for more than 5 stores). There is no annual billing. The vendor does not publish a setup time.",
    "sourceUrl": "https://trueprofit.io/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Core platform; on the Shopify App Store since July 2019.",
      "sourceUrl": "https://apps.shopify.com/trueprofit"
    },
    "amazon": {
      "value": "no",
      "note": "Amazon appears only as an ad-spend source, not as a sales channel.",
      "sourceUrl": "https://trueprofit.io/solutions/integrations",
      "badge": "Ad spend only"
    },
    "tiktok_shop": {
      "value": "yes",
      "note": "Direct TikTok Shop authorization syncs orders and products. One account can manage both Shopify and TikTok stores.",
      "sourceUrl": "https://help.trueprofit.io/en/article/onboarding-your-tiktok-shop-on-trueprofit-1fy4wxw/"
    },
    "wholesale_retail_or_erp": {
      "value": "partial",
      "note": "Shipping and 3PL cost integrations (ShipBob, ShipHero, ShipStation and others). No ERP, wholesale or POS sources.",
      "sourceUrl": "https://trueprofit.io/solutions/integrations"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta, Google, TikTok, Microsoft, Snapchat, Pinterest, X, AppLovin, Taboola, Amazon Ads and others.",
      "sourceUrl": "https://trueprofit.io/solutions/integrations"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "TrueProfit Pixel product analytics on Advanced and up. The Marketing Attribution module is Enterprise-only. Whether it is multi-touch is not stated.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "Not mentioned on vendor pages.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Real-time net profit with quantity- and zone-based COGS, shipping, taxes and custom costs.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "CLV cohort analysis on all plans.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "subscription_analytics": {
      "value": "unknown",
      "note": "No Recharge or subscription integration is listed.",
      "sourceUrl": "https://trueprofit.io/solutions/integrations"
    },
    "warehouse_access": {
      "value": "unknown",
      "note": "Only CSV export is listed. No warehouse or API destination is documented.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Custom metrics are listed as a feature on all plans. The formula flexibility was not verified.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "No SQL interface is mentioned.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "ai_assistant": {
      "value": "partial",
      "note": "A read-only MCP connection to ChatGPT, Claude and OpenClaw, Enterprise plan only per the pricing page. The Shopify listing also mentions AI extraction of costs from invoices.",
      "sourceUrl": "https://trueprofit.io/pricing"
    },
    "mobile_app": {
      "value": "yes",
      "note": "Free iOS and Android apps on all plans.",
      "sourceUrl": "https://trueprofit.io/pricing"
    }
  },
  "recentChanges": [
    {
      "date": "2026-05",
      "change": "Launched TrueProfit MCP, a read-only connection of profit data to ChatGPT, Claude and OpenClaw. The pricing page lists it as Enterprise-only.",
      "sourceUrl": "https://trueprofit.io/product-updates/trueprofit-mcp-turn-ai-into-your-second-finance-brain"
    },
    {
      "date": "2026",
      "change": "Current pricing has four order-based tiers ($35-$200) with capped per-order overage. When this pricing took effect was not determined.",
      "sourceUrl": "https://trueprofit.io/pricing"
    }
  ],
  "sources": [
    {
      "title": "TrueProfit pricing",
      "url": "https://trueprofit.io/pricing"
    },
    {
      "title": "TrueProfit integrations",
      "url": "https://trueprofit.io/solutions/integrations"
    },
    {
      "title": "TrueProfit on the Shopify App Store",
      "url": "https://apps.shopify.com/trueprofit"
    },
    {
      "title": "TrueProfit help: onboarding your TikTok Shop",
      "url": "https://help.trueprofit.io/en/article/onboarding-your-tiktok-shop-on-trueprofit-1fy4wxw/"
    },
    {
      "title": "TrueProfit MCP product update",
      "url": "https://trueprofit.io/product-updates/trueprofit-mcp-turn-ai-into-your-second-finance-brain"
    },
    {
      "title": "TrueProfit About us",
      "url": "https://trueprofit.io/about-us"
    }
  ],
  "lastReviewed": "2026-11-03"
};
